import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Save, ImageIcon, Trash2 } from "lucide-react";

const CATEGORIES = ["Ring", "Earring", "Necklace", "Pendant", "Nosepin", "Bracelet", "Bangle"];
const PURITIES = ["9KT", "14KT", "18KT"];
const COLOURS = ["Yellow", "White", "Rose"];
const STATUSES = ["NONE", "MEMO", "SOLD"];

const EMPTY = {
  product_code: "", category: "", sub_category: "", gold_colour: "",
  net_wt: "", base_purity: "18KT", diamond_wt: "", shape: "", pcs: "",
  distribution_catalog: true, status: "NONE", remark: "",
};

// Single dialog used to create OR edit a catalog product. If productCode is
// passed it loads & patches; otherwise it creates a new one.
export default function CatalogProductFormDialog({ open, onOpenChange, productCode = null, onSaved }) {
  const editing = !!productCode;
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState({ main: null, extras: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      api.get(`/catalog-products/${encodeURIComponent(productCode)}`)
        .then(({ data }) => {
          setForm({
            product_code: data.product_code, category: data.category || "", sub_category: data.sub_category || "",
            gold_colour: data.gold_colour || "", net_wt: data.net_wt ?? "", base_purity: data.base_purity || "18KT",
            diamond_wt: data.diamond_wt ?? "", shape: data.shape || "", pcs: data.pcs ?? "",
            distribution_catalog: !!data.distribution_catalog, status: data.status || "NONE",
            remark: data.remark || "",
          });
          setPhoto({ main: data.main_photo, extras: data.extra_photos || [] });
        })
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"));
    } else {
      setForm(EMPTY); setPhoto({ main: null, extras: [] });
    }
  }, [open, productCode, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.product_code.trim()) return toast.error("Product Code is required");
    if (!form.category.trim()) return toast.error("Category is required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        net_wt: parseFloat(form.net_wt) || 0,
        diamond_wt: parseFloat(form.diamond_wt) || 0,
        pcs: form.pcs === "" || form.pcs == null ? null : parseInt(form.pcs),
      };
      if (editing) {
        const { product_code, ...patch } = payload;  // eslint-disable-line no-unused-vars
        await api.patch(`/catalog-products/${encodeURIComponent(productCode)}`, patch);
        toast.success(`${productCode} updated`);
      } else {
        await api.post("/catalog-products", payload);
        toast.success(`${form.product_code} created`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file, type) => {
    if (!file || !editing) return;
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/catalog-products/photos/bulk", fd, {
        params: type === "extra" ? { _bypass: 1 } : {},
        headers: { "Content-Type": "multipart/form-data" },
      });
      // For single uploads via form, rely on filename matching. Provide a clear toast.
      if (data.matched_main + data.matched_extra === 0) {
        // Fallback: just attach via PATCH if the filename didn't match — manual filename
        toast.warning(`Filename '${file.name}' didn't match Product Code. Re-name to ${productCode}.${file.name.split('.').pop()} (or use _2 suffix for extras) and upload again.`);
      } else {
        toast.success("Photo uploaded");
        // refresh
        const r = await api.get(`/catalog-products/${encodeURIComponent(productCode)}`);
        setPhoto({ main: r.data.main_photo, extras: r.data.extra_photos || [] });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const deleteMain = async () => {
    if (!editing || !window.confirm("Remove main photo?")) return;
    try {
      await api.delete(`/catalog-products/${encodeURIComponent(productCode)}/photos/main`);
      setPhoto((p) => ({ ...p, main: null }));
      toast.success("Main photo removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const deleteExtra = async (url) => {
    if (!editing || !window.confirm("Remove this extra photo?")) return;
    try {
      await api.delete(`/catalog-products/${encodeURIComponent(productCode)}/photos/extra`, { params: { url } });
      setPhoto((p) => ({ ...p, extras: p.extras.filter((u) => u !== url) }));
      toast.success("Extra photo removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="catalog-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0D1B2A]">
            {editing ? `Edit ${productCode}` : "New Catalog Product"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Product Code *" testid="form-code">
              <Input value={form.product_code} onChange={(e) => set("product_code", e.target.value)} disabled={editing} placeholder="e.g. CCRG00001" data-testid="form-code-input" />
            </Field>
            <Field label="Category *" testid="form-category">
              <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="form-category-input"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sub Category">
              <Input value={form.sub_category} onChange={(e) => set("sub_category", e.target.value)} placeholder="e.g. Solitaire" />
            </Field>
            <Field label="Gold Colour">
              <Select value={form.gold_colour || "none"} onValueChange={(v) => set("gold_colour", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {COLOURS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Base Purity *">
              <Select value={form.base_purity} onValueChange={(v) => set("base_purity", v)}>
                <SelectTrigger data-testid="form-purity-input"><SelectValue /></SelectTrigger>
                <SelectContent>{PURITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Net Wt (g) *">
              <Input type="number" step="0.001" value={form.net_wt} onChange={(e) => set("net_wt", e.target.value)} data-testid="form-netwt-input" />
            </Field>
            <Field label="Diamond Wt (ct)">
              <Input type="number" step="0.001" value={form.diamond_wt} onChange={(e) => set("diamond_wt", e.target.value)} data-testid="form-diawt-input" />
            </Field>
            <Field label="Shape">
              <Input value={form.shape} onChange={(e) => set("shape", e.target.value)} placeholder="e.g. Round" />
            </Field>
            <Field label="Pieces">
              <Input type="number" value={form.pcs} onChange={(e) => set("pcs", e.target.value)} />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None / Available</SelectItem>
                  <SelectItem value="MEMO">Memo</SelectItem>
                  <SelectItem value="SOLD">Sold</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Remark">
            <Textarea value={form.remark} onChange={(e) => set("remark", e.target.value)} rows={2} />
          </Field>
          <div className="flex items-center gap-2">
            <Switch checked={form.distribution_catalog} onCheckedChange={(v) => set("distribution_catalog", v)} data-testid="form-dc-toggle" />
            <span className="text-sm text-slate-600">Show on public Distribution links</span>
          </div>

          {/* Photos (only after creation) */}
          {editing && (
            <div className="space-y-2 pt-3 border-t border-[#0D1B2A]/10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#b8893b]">Photos</p>
              <div className="grid grid-cols-4 gap-2">
                <PhotoTile url={photo.main} placeholder="Main" onDelete={deleteMain} onUpload={(f) => uploadPhoto(f, "main")} isMain testid="photo-main" />
                {photo.extras.map((u) => (
                  <PhotoTile key={u} url={u} onDelete={() => deleteExtra(u)} testid={`photo-extra-${u.slice(-12)}`} />
                ))}
                <PhotoTile placeholder="+ extra" onUpload={(f) => uploadPhoto(f, "extra")} testid="photo-add-extra" />
              </div>
              <p className="text-[11px] text-slate-400">For new uploads via this form, name the file <span className="font-mono">{productCode}.jpg</span> (main) or <span className="font-mono">{productCode}_2.jpg</span> (extra). Or use the bulk uploader on the Catalog Upload page.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-[#0D1B2A]/10">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="form-save">
              <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : (editing ? "Save changes" : "Create product")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function PhotoTile({ url, placeholder, onUpload, onDelete, isMain, testid }) {
  const input = React.useRef(null);
  return (
    <div className="relative group aspect-square border border-dashed border-slate-300 rounded bg-white flex items-center justify-center overflow-hidden" data-testid={testid}>
      {url ? (
        <>
          <img src={url} alt="" className="object-contain w-full h-full" />
          {onDelete && (
            <button type="button" onClick={onDelete} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          {isMain && <span className="absolute bottom-1 left-1 text-[8px] uppercase tracking-widest bg-[#b8893b] text-white px-1.5 py-0.5 rounded">Main</span>}
        </>
      ) : onUpload ? (
        <>
          <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onUpload(e.target.files[0]); }} />
          <button type="button" onClick={() => input.current?.click()} className="flex flex-col items-center justify-center w-full h-full text-[10px] text-slate-400 uppercase tracking-widest hover:bg-slate-50">
            <ImageIcon className="w-5 h-5 mb-1" /> {placeholder}
          </button>
        </>
      ) : null}
    </div>
  );
}
